import { ChangeDetectionStrategy, Component } from "@angular/core";
import { localize } from "@gtmhub/localization";
import { INotificationUser, IReactionNotification, IReactionNotificationDataItem } from "@gtmhub/notifications/models/notifications";
import { InboxTemplateComponent } from "../inbox-template.component";

const itemOwnerSuffixMap: Record<string, string> = {
  announcement_comment_reaction_created: "a_comment_on_your_announcement",
  task_comment_reaction_created: "a_comment_on_your_task",
  goal_comment_reaction_created: "a_comment_on_your_goal",
  metric_comment_reaction_created: "a_comment_on_your_key_result",
  insight_comment_reaction_created: "a_comment_on_your_insight",
  metric_snapshot_comment_reaction_created: "a_comment_on_your_kr_update",

  metric_snapshot_reaction_created: "your_kr_update",
  assigned_metric_snapshot_reaction_created: "an_automatic_update_on_your_kr",
  team_assigned_metric_snapshot_reaction_created: "an_automatic_update_on_your_teams_kr",
};

const mentionedSuffixMap: Record<string, string> = {
  announcement_comment_reaction_created: "a_comment_on_where_you_were_mentioned",
  task_comment_reaction_created: "a_comment_on_where_you_were_mentioned",
  goal_comment_reaction_created: "a_comment_on_where_you_were_mentioned",
  metric_comment_reaction_created: "a_comment_on_where_you_were_mentioned",
  insight_comment_reaction_created: "a_comment_on_where_you_were_mentioned",
  metric_snapshot_comment_reaction_created: "a_comment_on_where_you_were_mentioned",

  metric_snapshot_reaction_created: "your_kr_update",
  assigned_metric_snapshot_reaction_created: "an_automatic_update_on_your_kr",
  team_assigned_metric_snapshot_reaction_created: "an_automatic_update_on_your_teams_kr",
};

const commentOwnerSuffixMap: Record<string, string> = {
  announcement_comment_reaction_created: "your_comment_on_announcement",
  task_comment_reaction_created: "your_comment_on_task",
  goal_comment_reaction_created: "your_comment_on_goal",
  metric_comment_reaction_created: "your_comment_on_key_result",
  insight_comment_reaction_created: "your_comment_on_insight",
  metric_snapshot_comment_reaction_created: "your_comment_on_kr_update",

  metric_snapshot_reaction_created: "your_kr_update",
  assigned_metric_snapshot_reaction_created: "an_automatic_update_on_your_kr",
  team_assigned_metric_snapshot_reaction_created: "an_automatic_update_on_your_teams_kr",
};

@Component({
  selector: "reaction-inbox-template",
  templateUrl: "./reaction-inbox-template.component.html",
  styleUrls: ["../inbox-template.component.less"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReactionInboxTemplateComponent extends InboxTemplateComponent<IReactionNotification> {
  public get title(): string {
    const { item, actor } = this.notification.notificationData;
    const { reactedUsers } = item;
    const prefix = this.localizePrefix(reactedUsers, actor);
    const suffix = this.localizeSuffix(item);
    return `${prefix} ${suffix}`;
  }

  private localizePrefix(reactedUsers: INotificationUser[], actor: INotificationUser): string {
    if (!reactedUsers || reactedUsers.length <= 1) {
      return localize("reaction_notification_title", { names: actor.names });
    } else {
      return localize("reaction_notification_title_plural", { names: actor.names, length: reactedUsers.length - 1 });
    }
  }

  private localizeSuffix(item: IReactionNotificationDataItem): string {
    const targetItemName = item.targetItemName || "";
    switch (item.template) {
      case "comment_reaction_target_item_owner":
        return localize(itemOwnerSuffixMap[this.notification.operationType], { name: targetItemName });
      case "comment_reaction_mentioned":
        return localize(mentionedSuffixMap[this.notification.operationType], { name: targetItemName });
      case "comment_reaction":
        return localize(commentOwnerSuffixMap[this.notification.operationType], { name: targetItemName });
      default:
        return localize(commentOwnerSuffixMap[this.notification.operationType], { name: targetItemName });
    }
  }
}
